/**
 * Procedural Keeper tunables (design doc §19, §23). Bases and palettes are
 * data in `@heartpatch/shared` (`KEEPER_DATA`); this file holds the build
 * numbers every Keeper shares. Sizes are fractions of the Keeper's height
 * unless they say otherwise. All first guesses to judge in the Keeper
 * gallery (`/keepers.html`) and on the playtest devices.
 */
export const KEEPER = {
  /** World height of a standard (`body.height` 1) Keeper at scale 1; a hex tile is about 2 across. */
  height: 1.6, // TUNE
  /** Body depth as a fraction of its width. */
  torsoDepth: 0.8, // TUNE
  /** How far the head sinks into the body, as a fraction of its height. */
  neckSink: 0.12, // TUNE
  /** Shoes, as multiples of limb thickness: width, height, length. */
  shoe: [1.75, 1.05, 2.3], // TUNE
  /** The thinnest a limb gets, as a fraction of body size, so slim bodies still have toy-chunky limbs (#657). */
  minLimb: 0.09, // TUNE
  /** Legs, as a multiple of limb thickness (#657: chunky, so they read under the shorts). */
  leg: 1.2, // TUNE
  /** The shorts' legs, as a multiple of limb thickness. */
  shortsLeg: 1.55, // TUNE
  /** Arms (#657): chunky and rounded, coming out of short sleeves, hands joined on. */
  arm: {
    /** Skin arm thickness, as a multiple of limb thickness. */
    thick: 1.35, // TUNE
    /** The sleeve, as a multiple of the arm's thickness: wider, so the arm comes out of it. */
    sleeveThick: 1.3, // TUNE
    /** The hand, as a multiple of the arm's thickness. */
    hand: 1.15, // TUNE
    /** How far a hand sinks into the arm's end, as a fraction of the hand. */
    handSink: 0.25, // TUNE
    /** The torso's half width at the shoulder when it is a squarish capsule, of its full half width. */
    squareHalf: 0.97, // TUNE
    /** A hand stays this far (of its own size) outside the shorts' side. */
    handClear: 0.75, // TUNE
    /** How far out the arm hangs, as the sine of its angle: least, most (about 12° to 37°). */
    splay: [0.2, 0.6], // TUNE
  },
  /** Sleeves cover this much of the arm from the shoulder. */
  sleeve: 0.34, // TUNE
  /** Face features, as fractions of the head's own size. */
  face: {
    eyeSpread: 0.19, // TUNE: eye centre from the middle, of head width
    eyeHeight: -0.02, // TUNE: of head height, from its middle
    browHeight: 0.07, // TUNE: just above the eyes, clear of the fringe
    noseHeight: -0.12, // TUNE
    noseShift: 0.008, // TUNE: a touch off-centre, like a drawn line
    noseRollDeg: 8, // TUNE
    /** Brows, nose and smile are this deep (of head size): drawn on the face, not stuck on it. */
    lineDepth: 0.012, // TUNE
    mouthHeight: -0.22, // TUNE
    /** How far features stand out of the head surface, of their own depth. */
    standOut: 0.35, // TUNE
  },
  /**
   * Costume models (a costume's `model`, drawn instead of the Keeper): how
   * tall each stands as a multiple of the Keeper's own height, and where
   * the costume sets his eyes (the rest of his eye numbers are his own, in
   * `HOLLOW_MAN`). The Hollow Man is a head taller than a Keeper on the map
   * (the map draws him 0.85 to a Keeper's 0.55).
   */
  models: {
    'hollow-man': {
      heightRatio: 1.6, // TUNE
      /** His eyes, as fractions of his height: how far in front of his middle they sit. */
      eyeForward: 0.07, // TUNE
      /** The eye is a sphere, widened and flattened a little: width and depth to its height. */
      eyeWide: 1.25, // TUNE
      eyeDeep: 0.6, // TUNE
    },
  },
  /** Fixed toy colours (sRGB hex). */
  colors: {
    ink: '#3b2a3f', // TUNE: the squishies' plum ink
    /** The face's lines (#289): a step darker than ink so they read on the deepest skin. */
    faceLine: '#24151d', // TUNE
    white: '#ffffff',
  },
  /** Seeded wobble so two Keepers with one config are identical, but hair isn't ruler-straight. */
  jitter: { hairDeg: 6, hairSize: 0.06 }, // TUNE
} as const;

/** Detail levels, like squishies' (`procedural/config.ts`): `low` on the map, `high` in close-ups. */
export const KEEPER_LOD = {
  low: { bodyRings: 10, partRings: 6, partSegments: 10, modelSegments: 14 }, // TUNE
  high: { bodyRings: 20, partRings: 12, partSegments: 20, modelSegments: 28 }, // TUNE
} as const;

/** Where Keepers stand, and how big, in each place they show up. */
export const KEEPER_PLACES = {
  /** The map: next to the owner's Heart Seed, small (design doc §23). */
  map: {
    scale: 0.55, // TUNE
    /** Offset from the Heart Seed (world units). */
    offset: { x: 0.55, z: -0.25 }, // TUNE
    /** Leans back (radians) so the face shows under the steep map camera. */
    lean: 0.3, // TUNE
  },
  /**
   * Battles: at the player's back (design doc §23), like a coach: nearer the
   * camera than their squishy and off to the side, clear of the nameplates.
   */
  battle: {
    scale: 1.5, // TUNE
    /** Offset from the player's squishy (world units; +z is away from the camera). */
    offset: { x: -0.95, z: -1.9 }, // TUNE
    yaw: -0.6, // TUNE: turned towards the action
    lean: 0.35, // TUNE
  },
  /** The Keeper picker's preview (a close-up). */
  preview: {
    scale: 2.9, // TUNE
    lean: 0.5, // TUNE
    /** Where the camera looks (+z is away from it): the Keeper sits above the picker card. */
    lookAtZ: -2.3, // TUNE
    /**
     * Where no band is set (wardrobe, boutique): the tallest Keeper (world
     * units at scale 1, to the top of the hair) shown at full size, the
     * tallest starting look (Hazel, 1.8). A taller face, body and hair pair
     * (up to about 2.3, #372) shows smaller so its head stays on screen. The
     * picker fits the Keeper to the room above its card instead
     * (`KeeperPreview.setBand`). Preview only: the Keeper and its hash don't
     * change.
     */
    fitHeight: 1.8, // TUNE
  },
} as const;
